/**
 * Kosakata multi-mineral yang dipakai lebih dari satu layar.
 *
 * Persamaan respons adalah keterangan, bukan hiasan. "PEF 1,89 terukur lawan
 * 1,89 termodelkan" berarti dua hal yang sama sekali berbeda menurut apakah
 * yang memodelkannya campuran volumetrik atau penampang lintang per satuan
 * volume, dan yang membacanya berhak tahu yang mana. Karena itu labelnya ikut
 * ke mana pun angkanya pergi — panel model, uji satu titik, dan cetakan.
 *
 * Bawaannya sama persis dengan `DEFAULT_EQUATION` di `petro/multimin.py`.
 * Keduanya harus sepakat: sebuah model yang diam soal persamaannya akan
 * dihitung backend dengan bawaan di sana, dan bila layar menuliskan bawaan
 * yang lain, yang terbaca bukan yang dihitung.
 */
import type { MineralComponent } from "../types";

export const DEFAULT_EQUATION: Record<string, string> = {
  RHOB: "linear",
  GR: "linear",
  DTC: "linear",
  NPHI: "neutron",
  PEF: "pef",
  RT: "resistivity",
};

/** Nama pendek tiap persamaan, untuk lencana di sebelah nama alatnya. */
export const EQUATION_LABEL: Record<string, string> = {
  linear: "linear",
  pef: "U/ρ",
  neutron: "excavation",
  resistivity: "dual-water",
};

/** Satu kalimat tiap persamaan, untuk judul tooltip-nya. */
export const EQUATION_HINT: Record<string, string> = {
  linear: "A volumetric mixture: reading = Σ volume × endpoint.",
  pef: "PEF mixes by cross-section: U = PEF × ρ, then PEF = ΣvU / Σvρ.",
  neutron: "A volumetric mixture less gas excavation, proportional to φ²Sg.",
  resistivity: "Dual-water over porosity and saturation; its residual is computed in log₁₀ RT.",
};

/**
 * Batas sebuah komponen sebagaimana dibaca di tabel model.
 *
 * Yang dipaku ditulis sebagai satu angka, bukan sebagai selang yang kedua
 * ujungnya sama: "= 0,00" mengatakan mineral ini diketahui tidak ada di sumur
 * ini, sedangkan "0,00–0,00" terbaca seperti kelalaian mengisi kotak.
 */
export function describeBounds(component: MineralComponent): string {
  if (component.fixed !== undefined && component.fixed !== null) {
    return `= ${component.fixed.toFixed(2)}`;
  }
  const lower = component.min ?? 0;
  const upper = component.max ?? 1;
  if (lower === 0 && upper === 1) return "—";
  return `${lower.toFixed(2)}–${upper.toFixed(2)}`;
}

/**
 * Akar rata-rata kuadrat sisa sebuah alat, melewatkan cuplikan yang kosong.
 *
 * `null` ketika tidak ada satu pun cuplikan terisi — yang berbeda artinya dari
 * nol, dan menampilkan nol di sana akan membaca sebagai kecocokan sempurna.
 */
export function rmsResidual(values: (number | null)[] | undefined): number | null {
  if (!values) return null;
  let sum = 0;
  let count = 0;
  for (const value of values) {
    if (value === null || !Number.isFinite(value)) continue;
    sum += value * value;
    count += 1;
  }
  return count === 0 ? null : Math.sqrt(sum / count);
}

/**
 * Jalur SVG kurva incoherence pada sumbu kedalaman panel volumetrik.
 *
 * Dipisahkan ke sini karena pemasangan larik inilah yang pernah salah, dan
 * salahnya tidak terlihat. `incoherence` dan `md` dijarangkan backend ke 400
 * cuplikan, sedangkan `volumes` dan `volumes_md` — yang menjadi sumbu pita —
 * dijarangkan ke 120 baris. Keduanya membentang selang yang sama, jadi dua
 * larik yang panjangnya berbeda itu terbaca seolah sepadan, padahal cuplikan
 * ke-i dari yang satu bukan cuplikan ke-i dari yang lain.
 *
 * Memasangkannya menurut nomor urut karena itu menghasilkan `undefined` untuk
 * 280 cuplikan terakhir, dan jalurnya tertutup "NaN". Yang terjadi bukan
 * kurva yang lenyap: SVG menggambar jalur sampai titik galatnya lalu berhenti,
 * jadi yang tersisa di layar adalah 120 cuplikan pertama — sekitar sepertiga
 * awal selang — teregang sepanjang seluruh tinggi panel. Ia terbaca seperti
 * kurva incoherence yang wajar, hanya lebih halus, dan justru itu sebabnya ia
 * bertahan lama: satu-satunya yang mengeluh adalah konsol peramban. Yang
 * dipakai di sini adalah `md`, sumbu kedalaman milik incoherence sendiri.
 *
 * `range` tetap datang dari sumbu pita: keduanya harus membaca kedalaman yang
 * sama pada tinggi yang sama, dan itulah gunanya kurva ini duduk di sebelah
 * pitanya.
 */
export function incoherenceTrace(
  incoherence: (number | null)[],
  md: (number | null)[] | undefined,
  range: [number, number] | null,
): string {
  const finite = incoherence.filter(
    (value): value is number => value !== null && Number.isFinite(value),
  );
  if (!finite.length) return "";
  const max = Math.max(...finite, 0.05);

  // Sumbu kedalaman dipilih sekali di muka, bukan per cuplikan: sebuah jalur
  // yang sebagian titiknya memakai kedalaman dan sebagian lagi nomor urut
  // bukan kurva yang tergambar setengah benar melainkan kurva yang salah.
  const span = range ? range[1] - range[0] : 0;
  const byDepth = Boolean(md && md.length === incoherence.length && range && span);

  const points: string[] = [];
  for (let index = 0; index < incoherence.length; index++) {
    const value = incoherence[index];
    if (value === null || !Number.isFinite(value)) continue;

    let fraction: number;
    if (byDepth) {
      const depth = md![index];
      if (depth === null || !Number.isFinite(depth)) continue;
      fraction = (depth - range![0]) / span;
    } else {
      fraction = index / Math.max(incoherence.length - 1, 1);
    }

    const x = (value / max) * 96 + 2;
    points.push(`${x.toFixed(1)},${(fraction * 1000).toFixed(0)}`);
  }

  return points
    .map((point, index) => (index === 0 ? `M${point}` : ` L${point}`))
    .join("");
}
